import React from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Breadcrumbs, Link as MuiLink, Divider, Stack, Button,
} from '@mui/material';
import { ArrowBack, CurrencyRupee, EventBusy, Block, Payment, ContactSupport } from '@mui/icons-material';
import { C } from './vps/theme';

const RefundPolicy = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ backgroundColor: C.bgSection, minHeight: '100vh', py: { xs: 5, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Navigation / Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{
              color: C.primary,
              fontWeight: 600,
              mb: 2,
              '&:hover': { backgroundColor: C.primaryLight },
            }}
          >
            Back to Home
          </Button>

          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <MuiLink component={RouterLink} to="/" underline="hover" color="inherit" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
              Home
            </MuiLink>
            <Typography color="text.primary" sx={{ fontSize: '0.9rem', fontWeight: 700, color: C.primary }}>
              Refund Policy
            </Typography>
          </Breadcrumbs>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 800,
              color: C.textPrimary,
              mb: 1,
              letterSpacing: '-0.02em',
            }}
          >
            Premium Backlink Services Refund Policy
          </Typography>
          <Typography sx={{ color: C.textSecondary, fontSize: '1rem', maxWidth: 720 }}>
            Last updated: January 2026. This policy explains how refunds, cancellations, and billing disputes
            are handled for backlink package orders processed via Razorpay Payment Gateway.
          </Typography>
        </Box>

        {/* Content Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5, md: 6 },
            backgroundColor: '#FFFFFF',
            border: `1px solid ${C.border}`,
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
          }}
        >
          <Stack spacing={4}>
            {/* Section 1 */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <CurrencyRupee sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  1. Money-Back Guarantee
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75, mb: 1.5 }}>
                We offer a 7-day money-back guarantee on all backlink packages,
                starting from the date your order is confirmed, provided outreach has not yet begun:
              </Typography>
              <Typography component="div" sx={{ color: C.textSecondary, lineHeight: 1.75, pl: 2 }}>
                • Eligible customers may request a full refund within 7 calendar days of initial activation.<br />
                • The guarantee applies once per customer, per service, and is intended for first-time purchases only.<br />
                • Refunds are credited back to the original payment method through Razorpay Payment Gateway within 5–7 business days of approval.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: C.border }} />

            {/* Section 2 */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Block sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  2. Non-Refundable Items
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75, mb: 1.5 }}>
                Certain charges fall outside the money-back guarantee and are non-refundable:
              </Typography>
              <Typography component="div" sx={{ color: C.textSecondary, lineHeight: 1.75, pl: 2 }}>
                • Backlinks or guest posts already live and delivered as part of your order.<br />
                • Custom outreach work already completed for a specific target site.<br />
                • Guest posts or content already published as part of your order.<br />
                • Renewal payments for services used beyond the initial 7-day guarantee window.<br />
                • Add-on services (extra guest posts, expedited delivery) already delivered.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: C.border }} />

            {/* Section 3 */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <EventBusy sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  3. Cancellations
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75 }}>
                You may cancel an unstarted order at any time from your account dashboard or by contacting
                our support team. Cancellations take effect at the end of the current billing cycle, and
                further work on your campaign is stopped once the
                service term ends. We recommend downloading a backup of your data before cancelling.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: C.border }} />

            {/* Section 4 */}
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Payment sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  4. Refund Processing & Chargebacks
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75 }}>
                Approved refunds are processed securely through <strong>Razorpay Payment Gateway</strong> back
                to the original source of payment; we do not issue refunds in cash or to a different account.
                Processing times may vary depending on your bank or card issuer. If you initiate a chargeback
                with your bank instead of contacting our support team first, your account access may be
                suspended while the dispute is investigated.
              </Typography>
            </Box>

            {/* <Divider sx={{ borderColor: C.border }} /> */}

            {/* Section 5 */}
            {/* <Box>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <ContactSupport sx={{ color: C.primary, fontSize: 26 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary }}>
                  5. Request a Refund
                </Typography>
              </Stack>
              <Typography sx={{ color: C.textSecondary, lineHeight: 1.75, mb: 2 }}>
                To request a refund or cancellation, please reach out to our billing team with your order ID
                and reason for the request:
              </Typography>
              <Box
                sx={{
                  p: 2.5,
                  backgroundColor: C.bgSection,
                  borderRadius: '12px',
                  border: `1px solid ${C.border}`,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: C.textPrimary }}>
                  Premium Backlink Services Billing Desk
                </Typography>
                <Typography variant="body2" sx={{ color: C.textSecondary, mt: 0.5 }}>
                  Email: support@premiumbacklinkservices.com | Available 24/7
                </Typography>
              </Box>
            </Box> */}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};

export default RefundPolicy;
